(() => {
  if (customElements.get('tb-menu-nav')) return;

  class TbMenuNav extends HTMLElement {
    connectedCallback() {
      const rootPanel = this.querySelector('.tb-menu-panel[data-panel]');
      const rootId = rootPanel?.dataset.panel || 'root';
      const rootTitle = rootPanel?.dataset.panelTitle || 'Main';
      this.stack = [{ id: rootId, title: rootTitle }];
      this.templates = new Map();

      this._registerTemplates(this);

      this.addEventListener('click', this._onClick);

      this._dialog = this.closest('dialog');
      if (this._dialog) {
        this._dialog.addEventListener('close', () => {
          this.reset();
          this._releaseSearchResults();
          this._searchObserver?.disconnect();
        });
        // Native <dialog> has no "open" event, so watch the `open` attribute
        // directly to know the instant the menu is shown.
        this._dialogOpenObserver = new MutationObserver(() => {
          if (this._dialog.open) {
            this._claimSearchResults();
            this._watchSearchResults();
            // The browser's native <dialog> autofocus (or the theme's own
            // drawer script) can focus the first focusable element inside —
            // which happens to be the search input — the instant the menu
            // opens. That pops the mobile keyboard and shows a blinking
            // cursor before the shopper has tapped anything, which we only
            // want once they actually choose to search. Blur it straight
            // back out.
            const searchInput = this.querySelector('[data-input-search]');
            if (searchInput && document.activeElement === searchInput) {
              searchInput.blur();
            }
          } else {
            this._searchObserver?.disconnect();
          }
        });
        this._dialogOpenObserver.observe(this._dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this._searchObserver?.disconnect();
      this._dialogOpenObserver?.disconnect();
    }

    get currentId() {
      return this.stack[this.stack.length - 1]?.id || 'root';
    }

    _registerTemplates(root) {
      if (!root) return;
      root.querySelectorAll('template[data-panel-template]').forEach((tpl) => {
        const id = tpl.dataset.panelTemplate;
        if (id && !this.templates.has(id)) {
          this.templates.set(id, tpl);
        }
        if (tpl.content) {
          this._registerTemplates(tpl.content);
        }
      });
    }

    _onClick = (event) => {
      const openBtn = event.target.closest('[data-menu-open]');
      if (openBtn) {
        event.preventDefault();
        const id = openBtn.dataset.menuOpen;
        const title = openBtn.dataset.menuTitle ||
                      openBtn.querySelector('.tb-menu-row__title')?.textContent.trim() ||
                      '';
        this.open(id, title);
        return;
      }

      const toBtn = event.target.closest('[data-menu-to]');
      if (toBtn) {
        event.preventDefault();
        this.goTo(toBtn.dataset.menuTo);
        return;
      }

      const backBtn = event.target.closest('[data-menu-back]');
      if (backBtn) {
        event.preventDefault();
        this.back();
      }
    };

    getPanel(id) {
      return this.querySelector(`:scope > .tb-menu-panel[data-panel="${CSS.escape(id)}"]`) ||
             this.querySelector(`.tb-menu-panel[data-panel="${CSS.escape(id)}"]`);
    }

    hydrate(id) {
      let panel = this.getPanel(id);
      if (panel) return panel;

      let tpl = this.templates.get(id);
      if (!tpl) {
        this._registerTemplates(this);
        tpl = this.templates.get(id);
      }
      if (!tpl) return null;

      const clone = tpl.content.cloneNode(true);
      this._registerTemplates(clone);
      this.appendChild(clone);
      return this.getPanel(id);
    }

    _updateBreadcrumbs(panel) {
      if (!panel) return;

      // Breadcrumb is shown from second view onwards: depth >= 2
      // where stack = [Main (0), Category (1), Subitem (2)...]
      let breadcrumbEl = panel.querySelector('.tb-menu-breadcrumb');

      if (this.stack.length < 2) {
        if (breadcrumbEl) breadcrumbEl.hidden = true;
        return;
      }

      if (!breadcrumbEl) {
        breadcrumbEl = document.createElement('nav');
        breadcrumbEl.className = 'tb-menu-breadcrumb';
        breadcrumbEl.setAttribute('aria-label', 'Breadcrumbs');
        const banner = panel.querySelector('.tb-menu-panel__banner');
        if (banner && banner.nextSibling) {
          panel.insertBefore(breadcrumbEl, banner.nextSibling);
        } else if (banner) {
          panel.appendChild(breadcrumbEl);
        } else {
          panel.prepend(breadcrumbEl);
        }
      }

      breadcrumbEl.hidden = false;
      breadcrumbEl.innerHTML = '';

      const list = document.createElement('ol');
      list.className = 'tb-menu-breadcrumb__list';

      // Include all items in stack: items[0] is 'Main', items[1] is 'Christmas Hampers', items[2] is 'For Her'...
      const items = this.stack;
      items.forEach((item, index) => {
        const isLast = index === items.length - 1;
        const li = document.createElement('li');
        li.className = 'tb-menu-breadcrumb__item' + (isLast ? ' tb-menu-breadcrumb__item--current' : '');

        if (isLast) {
          li.setAttribute('aria-current', 'page');
          const span = document.createElement('span');
          span.className = 'tb-menu-breadcrumb__text';
          span.textContent = item.title;
          li.appendChild(span);
        } else {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'tb-menu-breadcrumb__btn';
          btn.dataset.menuTo = item.id;
          btn.textContent = item.title;
          li.appendChild(btn);
        }
        list.appendChild(li);

        if (!isLast) {
          const sep = document.createElement('li');
          sep.className = 'tb-menu-breadcrumb__sep';
          sep.setAttribute('aria-hidden', 'true');
          sep.innerHTML = '<svg viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.5L4.5 5L1 8.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          list.appendChild(sep);
        }
      });

      breadcrumbEl.appendChild(list);

      requestAnimationFrame(() => {
        breadcrumbEl.scrollTo({ left: 9999, behavior: 'instant' });
      });
    }

    open(id, title) {
      if (!id || this.currentId === id) return;
      const target = this.hydrate(id);
      if (!target) return;

      if (!title) {
        title = target.dataset.panelTitle ||
                target.querySelector('.tb-menu-panel__banner-title')?.textContent.trim() ||
                '';
      }

      const current = this.getPanel(this.currentId);
      this.stack.push({ id, title });

      this._updateBreadcrumbs(target);
      this._transition(current, target, 'forward');
    }

    goTo(id) {
      if (!id || this.currentId === id) return;
      const targetIndex = this.stack.findIndex((item) => item.id === id);
      if (targetIndex === -1) {
        this.open(id);
        return;
      }

      const from = this.stack[this.stack.length - 1];
      const to = this.stack[targetIndex];
      const fromEl = this.getPanel(from.id);
      const toEl = this.getPanel(to.id);

      const popped = this.stack.splice(targetIndex + 1);
      popped.forEach((item) => {
        if (item.id !== from.id) {
          const el = this.getPanel(item.id);
          el?.classList.remove('is-active', 'is-incoming-forward', 'is-incoming-back', 'is-outgoing-forward', 'is-outgoing-back');
        }
      });

      if (toEl) {
        this._updateBreadcrumbs(toEl);
      }
      this._transition(fromEl, toEl, 'back');
    }

    back() {
      if (this.stack.length <= 1) return;
      const from = this.stack.pop();
      const to = this.stack[this.stack.length - 1];
      const fromEl = this.getPanel(from.id);
      const toEl = this.getPanel(to.id);

      if (toEl) {
        this._updateBreadcrumbs(toEl);
      }
      this._transition(fromEl, toEl, 'back');
    }

    reset() {
      if (this.stack.length <= 1) return;
      const root = this.stack[0];
      this.stack.slice(1).forEach((item) => {
        const el = this.getPanel(item.id);
        el?.classList.remove('is-active', 'is-incoming-forward', 'is-incoming-back', 'is-outgoing-forward', 'is-outgoing-back');
      });
      this.getPanel(root.id)?.classList.add('is-active');
      this.stack = [root];
    }

    openWithAncestors(id) {
      if (this.currentId === id) return;
      if (this.stack.some((item) => item.id === id)) {
        this.goTo(id);
        return;
      }

      const chain = [];
      let currId = id;
      const visited = new Set();
      while (currId && currId !== 'root' && !visited.has(currId)) {
        visited.add(currId);
        const panel = this.hydrate(currId);
        const title = panel?.dataset.panelTitle ||
                      panel?.querySelector('.tb-menu-panel__banner-title')?.textContent.trim() ||
                      '';
        chain.unshift({ id: currId, title });

        const trigger = this.querySelector(`[data-menu-open="${CSS.escape(currId)}"]`);
        const parentPanel = trigger?.closest('.tb-menu-panel');
        const parentId = parentPanel?.dataset.panel;
        if (parentId && parentId !== currId) {
          currId = parentId;
        } else {
          break;
        }
      }

      const rootPanel = this.querySelector('.tb-menu-panel[data-panel="root"]');
      const rootTitle = rootPanel?.dataset.panelTitle || 'Main';
      const root = { id: 'root', title: rootTitle };

      const current = this.getPanel(this.currentId);
      const target = this.hydrate(id);

      this.stack = [root, ...chain];
      this._updateBreadcrumbs(target);
      this._transition(current, target, 'forward');
    }

    // Relocate Snize's results dropdown into this dialog — but ONLY while the
    // menu is actually open, and only for the shared results element itself.
    // Snize uses one shared dropdown for every search input on the site
    // (this menu's, the header's, etc.), so grabbing it unconditionally would
    // strand it inside this (closed) dialog and break search everywhere else
    // on the page. It's returned to <body> as soon as the menu closes.
    _watchSearchResults() {
      this._searchObserver = new MutationObserver(() => this._claimSearchResults());
      this._searchObserver.observe(document.body, { childList: true, subtree: true });
    }

    _findSearchResultsEl() {
      return document.getElementById('snize-instant-search-results')
        || document.querySelector('.snize-ac-results');
    }

    _claimSearchResults() {
      if (!this._dialog?.open) return;
      const searchWrap = this.querySelector('.tb-menu-search');
      const el = this._findSearchResultsEl();
      if (!searchWrap || !el || searchWrap.contains(el)) return;
      searchWrap.appendChild(el);
      el.classList.add('tb-menu-search__portal-results');
    }

    _releaseSearchResults() {
      const searchWrap = this.querySelector('.tb-menu-search');
      const el = this._findSearchResultsEl();
      if (!searchWrap || !el || !searchWrap.contains(el)) return;
      el.classList.remove('tb-menu-search__portal-results');
      document.body.appendChild(el);
    }

    _transition(fromEl, toEl, direction) {
      if (!toEl || toEl === fromEl) return;
      this.classList.add('is-animating');
      toEl.classList.add(direction === 'forward' ? 'is-incoming-forward' : 'is-incoming-back');
      toEl.classList.add('is-active');
      const body = toEl.querySelector('.tb-menu-panel__body');
      if (body) body.scrollTop = 0;

      requestAnimationFrame(() => {
        toEl.classList.remove('is-incoming-forward', 'is-incoming-back');
        if (fromEl) {
          fromEl.classList.remove('is-active');
          fromEl.classList.add(direction === 'forward' ? 'is-outgoing-forward' : 'is-outgoing-back');
        }
      });

      const cleanup = () => {
        fromEl?.classList.remove('is-outgoing-forward', 'is-outgoing-back');
        this.classList.remove('is-animating');
        toEl.removeEventListener('transitionend', cleanup);
      };
      toEl.addEventListener('transitionend', cleanup);
      setTimeout(cleanup, 400); // safety net if transitionend doesn't fire
    }
  }

  customElements.define('tb-menu-nav', TbMenuNav);

  // Global trigger handler: Open mobile menu drawer when clicking .custom-drawer-trigger
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.custom-drawer-trigger, [data-menu-mobile-open]');
    if (trigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const dialog = document.getElementById('menu_mobile');
      if (!dialog) return;
      const drawer = dialog.closest('hdt-drawer') || dialog.parentElement;
      if (dialog.open) {
        if (drawer && typeof drawer.close === 'function') drawer.close();
        else if (typeof dialog.close === 'function') dialog.close();
      } else {
        if (drawer && typeof drawer.open === 'function') drawer.open();
        else if (typeof dialog.showModal === 'function') dialog.showModal();
      }
    }
  }, true);

  // KSP Discount Codes Click-to-Copy
  function copyKspDiscount(text) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => fallbackKspCopy(text));
    } else {
      fallbackKspCopy(text);
    }
  }

  function fallbackKspCopy(text) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      console.warn('KSP copy error:', e);
    }
  }

  document.addEventListener('click', (event) => {
    const item = event.target.closest('.tb-menu-ksp__item');
    if (!item) return;

    event.preventDefault();
    const code = item.dataset.code;
    if (!code) return;

    copyKspDiscount(code);

    if (navigator.vibrate) {
      try { navigator.vibrate(25); } catch (_) {}
    }

    document.querySelectorAll('.tb-menu-ksp__item').forEach(btn => btn.classList.remove('is-copied'));
    item.classList.add('is-copied');

    clearTimeout(item._copyTimeout);
    item._copyTimeout = setTimeout(() => {
      item.classList.remove('is-copied');
    }, 1800);
  });

  // Shopify Theme Editor support
  if (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const nav = document.querySelector('tb-menu-nav');
      if (!nav) return;
      const dialog = document.getElementById('menu_mobile');
      if (dialog) {
        const drawer = dialog.closest('hdt-drawer') || dialog.parentElement;
        if (drawer && typeof drawer.open === 'function' && !dialog.open) {
          drawer.open();
        }
      }
      if (nav.templates.has(blockId) || nav.getPanel(blockId)) {
        nav.openWithAncestors(blockId);
      }
    });

    document.addEventListener('shopify:block:deselect', () => {
      const nav = document.querySelector('tb-menu-nav');
      if (nav) nav.reset();
    });
  }
})();
